import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useStatisticsApi } from '../hooks/useStatisticsApi';
import { IncomeStatisticsResponse, ProductStatisticsItem, ServiceStatisticsItem, PaymentMethodStatisticsItem } from '../types/statistics';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import toast from 'react-hot-toast';
import DateRangeFilter from '../components/statistics/DateRangeFilter';
import IncomeSummaryCards from '../components/statistics/IncomeSummaryCards';
import IncomeBarChart from '../components/statistics/IncomeBarChart';
import ProductsDonutChart from '../components/statistics/ProductsDonutChart';
import ServicesDonutChart from '../components/statistics/ServicesDonutChart';
import PaymentMethodsDonutChart from '../components/statistics/PaymentMethodsDonutChart';

const StatisticsPage: React.FC = () => {
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState<Date>(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState<Date>(endOfMonth(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Statistics data
  const [incomeData, setIncomeData] = useState<IncomeStatisticsResponse | null>(null);
  const [productsData, setProductsData] = useState<ProductStatisticsItem[]>([]);
  const [servicesData, setServicesData] = useState<ServiceStatisticsItem[]>([]);
  const [paymentMethodsData, setPaymentMethodsData] = useState<PaymentMethodStatisticsItem[]>([]);

  const statisticsApi = useStatisticsApi();

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate date range
      if (startDate > endDate) {
        setError('Start date cannot be after end date');
        setLoading(false);
        return;
      }

      const startDateString = format(startDate, 'yyyy-MM-dd');
      const endDateString = format(endDate, 'yyyy-MM-dd');

      const [income, products, services, paymentMethods] = await Promise.all([
        statisticsApi.fetchIncomeStatistics({ startDate: startDateString, endDate: endDateString }),
        statisticsApi.fetchProductsStatistics({ startDate: startDateString, endDate: endDateString }),
        statisticsApi.fetchServicesStatistics({ startDate: startDateString, endDate: endDateString }),
        statisticsApi.fetchPaymentMethodsStatistics({ startDate: startDateString, endDate: endDateString }),
      ]);

      setIncomeData(income);
      setProductsData(products);
      setServicesData(services);
      setPaymentMethodsData(paymentMethods);
    } catch (err: any) {
      console.error('Error loading statistics:', err);
      setError(err.message || 'Failed to load statistics');
      toast.error(err.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const handleStartDateChange = (newValue: Date | null) => {
    if (newValue) {
      setStartDate(newValue);
      // If new start date is after end date, update end date
      if (newValue > endDate) {
        setEndDate(newValue);
      }
    }
  };

  const handleEndDateChange = (newValue: Date | null) => {
    if (newValue) {
      setEndDate(newValue);
      // If new end date is before start date, update start date
      if (newValue < startDate) {
        setStartDate(newValue);
      }
    }
  };

  if (loading && !incomeData) {
    return (
      <Container maxWidth="xl">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            {t('common.loading')}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: { xs: 2, md: 3 }, px: { xs: 1, md: 0 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 2, md: 4 } }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            {t('statistics.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
            {t('statistics.subtitle')}
          </Typography>
        </Box>

        {/* Date Range Filter */}
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        {incomeData && <IncomeSummaryCards incomeData={incomeData} />}

        {/* Charts */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
            gap: { xs: 2, md: 3 },
          }}
        >
            {/* Income Bar Chart */}
            <Box>
              {incomeData ? (
                <IncomeBarChart incomeData={incomeData} />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('statistics.noData')}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Services Donut Chart */}
            <Box>
              <ServicesDonutChart servicesData={servicesData} />
            </Box>

            {/* Products Donut Chart */}
            <Box>
              <ProductsDonutChart productsData={productsData} />
            </Box>

          {/* Payment Methods Donut Chart */}
          <Box>
            <PaymentMethodsDonutChart paymentMethodsData={paymentMethodsData} />
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default StatisticsPage;
